import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, MapPin, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTrip } from "@/lib/store";

export const copyText = async (text, label = "Copied") => {
  try { await navigator.clipboard.writeText(text); toast.success(label); } catch {
    const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); toast.success(label); } catch { toast.error("Couldn't copy — long-press the text instead"); }
    document.body.removeChild(ta);
  }
};

export const mapsUrl = (provider, hotel) => {
  const q = encodeURIComponent(`${hotel.name} ${hotel.address}`);
  const cn = encodeURIComponent(hotel.addressChinese);
  switch (provider) {
    case "google": return `https://www.google.com/maps/search/?api=1&query=${q}`;
    case "amap": return `https://uri.amap.com/search?keyword=${cn}`;
    case "baidu": return `https://map.baidu.com/search/${cn}`;
    default: return `https://maps.apple.com/?q=${q}`;
  }
};

export function DriverCard({ hotel, onClose }) {
  const { trip } = useTrip();
  const [copied, setCopied] = useState(null);
  const copy = async (text, key, label) => { await copyText(text, label); setCopied(key); setTimeout(() => setCopied(null), 1500); };
  return (
    <AnimatePresence>
      {hotel && (
        <motion.div data-testid="show-driver-card" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }} transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] flex flex-col bg-white text-[var(--ink)]" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="flex items-center justify-between border-b px-4 py-3">
            <button data-testid="driver-back-btn" onClick={onClose} className="tap inline-flex items-center gap-2 rounded-full bg-[var(--parchment-2)] px-4 py-2 text-sm font-semibold"><ArrowLeft className="h-4 w-4" /> Back</button>
            <span className="eyebrow">Show this to your driver · 请带我们去这里</span>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-8 md:mx-auto md:w-full md:max-w-3xl">
            <p className="eyebrow mb-2">{hotel.city}</p>
            <h1 data-testid="driver-hotel-name" className="font-display text-4xl font-semibold leading-tight">{hotel.name}</h1>
            {hotel.subtitle && <p className="mt-1 text-base text-[var(--ink-muted)]">{hotel.subtitle}</p>}
            <p data-testid="driver-chinese-address" className="font-cn mt-8 text-[2.6rem] font-bold leading-[1.25] text-[var(--jade-deep)] sm:text-5xl md:text-6xl" style={{ wordBreak: "keep-all" }}>{hotel.addressChinese}</p>
            <p data-testid="driver-english-address" className="mt-8 text-lg leading-relaxed text-[var(--ink-muted)] md:text-xl">{hotel.address}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 border-t bg-[var(--parchment)] p-4 md:mx-auto md:w-full md:max-w-3xl">
            <button data-testid="driver-copy-chinese-btn" onClick={() => copy(hotel.addressChinese, "cn", "Chinese address copied")} className="tap inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-[var(--jade)] px-4 text-sm font-semibold text-[var(--parchment)]">
              {copied === "cn" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy Chinese address
            </button>
            <button data-testid="driver-copy-full-btn" onClick={() => copy(`${hotel.name}\n${hotel.addressChinese}\n${hotel.address}`, "full", "Full address copied")} className="tap inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border-2 border-[var(--jade)] px-4 text-sm font-semibold text-[var(--jade)]">
              {copied === "full" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy full address
            </button>
            <a data-testid="driver-open-maps-btn" href={mapsUrl(trip?.settings?.mapsProvider, hotel)} target="_blank" rel="noreferrer" className="tap inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl border px-4 text-sm font-semibold"><MapPin className="h-4 w-4" /> Open in Maps</a>
            <button data-testid="driver-close-btn" onClick={onClose} className="tap inline-flex min-h-[56px] items-center justify-center rounded-2xl border px-4 text-sm font-semibold">Back</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
